import { easeInOutQuint } from 'just-curves';
import { waitFor } from '../../../CodesThatsGottaGoSomewhereElse';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { ObjectManager } from '../../../concepts/objects';
import { Camera } from '../../canvas/Camera';
import { ConfettiCannon } from '../../canvas/ConfettiCannon';
import { EditorSession } from '../../canvas/EditorSession';
import { Selection } from '../../canvas/Selection';
import { clearSelection } from '../../canvas/commands/SelectionCommands';
import { Phys } from '../../canvas/entities/Phys';
import { Physical, RelativeTo } from '../../canvas/entities/Physical';
import { ContentInjector } from '../../canvas/injection/ContentInjector';
import { kioskPlayRecording } from '../../canvas/kiosk/KioskCommands';
import { ParticipantManager } from '../../canvas/participants/ParticipantManager';
import { PropertyStripInfo } from '../../canvas/properties/PropertyStripInfo';
import { meetingSetTools, meetingStart } from '../../canvas/workflow/MeetingCommands';
import { AlertManager } from '../../packages/alerts/AlertManager';
import { Sticky } from '../../packages/paper/Sticky';
import { CanvasPing } from '../../packages/presentation/Ping';
import { PingStore } from '../../packages/presentation/PingStore';
import { pingUI } from '../../packages/presentation/PresentCommands';
import { Zone } from '../../packages/zones/Zone';
import { PopupManager } from '../../popups/PopupManager';
import { PreferencesModel } from '../../preferences/PreferencesModel';
import { confettiScreen } from '../OnboardingCommands';
import { TutorialGoal, createTutorialGoals } from './TutorialGoals';
import {
    deleteAllShapes,
    deleteAllText,
    popInInstructions,
    revealStickiesWithDelay,
    waitForCameraLeftRight,
    waitForCameraUpDown,
    waitForCameraZoom,
    waitForConfetti,
    waitForElementClick,
    waitForState
} from './TutorialHelpers';
import { tutorialPopup } from './TutorialPopup';
import {
    ConfettiInstructions,
    CreateInstructions,
    DeleteInstructions,
    GoodBadZones,
    MoveInstructions,
    PingInstructions,
    RevealInstructions,
    SelectInstructions,
    StartInstructions,
    VideoProp1,
    VideoProp2,
    VideoProp3
} from './TutorialPrefabs';
import { TutorialStep } from './TutorialStep';

const host = { state: false };

const setHost = (value: boolean) => {
    host.state = value;
};

const intro = TutorialStep('intro', async r => {
    const camera = r.call(Camera);
    const dispatch = r.call(CommandDispatcher);
    const objects = r.call(ObjectManager);
    const popups = r.call(PopupManager);
    const preferences = r.call(PreferencesModel);
    const session = r.call(EditorSession);

    preferences.mutate({ haug: false });
    session.update(PropertyStripInfo, { mode: 'disabled' });

    const bounds = Phys.from(objects.getAll(Zone)).bounds().inflate(200);
    await camera.animateToFrame(bounds, 500, easeInOutQuint);

    await popups.invoke(tutorialPopup(
        'Welcome to Ludi',
        [
            'This interactive tutorial will teach you the basics of Ludi.',
            'Are you hosting?',
        ],
        [
            {
                label: 'Yes',
                onClick: () => setHost(true),
            },
            {
                label: 'No',
                onClick: () => setHost(false),
            },
        ]
    ));
});

const startMeeting = TutorialStep('addZones', async r => {
    const alerts = r.call(AlertManager);
    const camera = r.call(Camera);
    const dispatch = r.call(CommandDispatcher);
    const injector = r.call(ContentInjector);
    const objects = r.call(ObjectManager);

    injector.injectNative(GoodBadZones, { useRefAsId: true });
    dispatch(clearSelection);

    if (host.state === true) {
        const bounds = Phys.from(objects.getAll(Zone)).bounds().inflate(200);
        const bounds2 = {
            ...bounds,
            top: bounds.top - 400,
        };
        await camera.animateToFrame(bounds2, 2000, easeInOutQuint);

        injector.injectNative(StartInstructions);
        dispatch(clearSelection);

        await popInInstructions(r);

        const setGoal = createTutorialGoals(alerts, [
            TutorialGoal('LL', 'Lock your layout'),
            TutorialGoal('SM', 'Start the meeting'),
        ],
            [0, 8]);

        await waitForElementClick('#lock-layout');
        setGoal('LL', true);

        await waitForElementClick('#start-meeting');
        setGoal('SM', true);
    }

    if (host.state === false) {
        await dispatch(meetingStart, true);
    };

    await waitFor(100);

    await dispatch(meetingSetTools, [
        'default',
        'stickyTool',
        'pingTool',
        'confettiTool',
    ]);

    deleteAllText(objects);

    await waitFor(50);
});

const panZoom = TutorialStep('panZoom', async r => {
    const alerts = r.call(AlertManager);
    const camera = r.call(Camera);
    const dispatch = r.call(CommandDispatcher);
    const injector = r.call(ContentInjector);
    const objects = r.call(ObjectManager);
    const popups = r.call(PopupManager);

    await popups.invoke(tutorialPopup(
        'Board Navigation',
        [
            'Behind me is the whiteboard where you and your team interact and collaborate.',
            'Let\'s learn how to move around the board...',
        ],
        'Next'
    ));

    if (host.state === false) {
        const bounds = Phys.from(objects.getAll(Zone)).bounds().inflate(200);
        const bounds2 = {
            ...bounds,
            top: bounds.top - 400,
        };
        await camera.animateToFrame(bounds2, 2000, easeInOutQuint);
    }

    injector.injectNative(MoveInstructions);
    injector.injectNative(VideoProp1);
    dispatch(clearSelection);

    await popInInstructions(r);

    {
        const set = createTutorialGoals(alerts, [
            TutorialGoal('LR', 'Move the camera left & right'),
            TutorialGoal('UD', 'Move the camera up & down'),
        ],
            [1, 8]);

        await Promise.all([
            waitForCameraLeftRight(camera, 75).then(() => set('LR', true)),
            waitForCameraUpDown(camera, 75).then(() => set('UD', true)),
        ]);
    }

    await waitFor(500);

    {
        const set = createTutorialGoals(alerts, [
            TutorialGoal('ZI', 'Zoom the camera in'),
            TutorialGoal('ZO', 'Zoom the camera out'),
        ],
            [1, 8]);

        await Promise.all([
            waitForCameraZoom(camera, 1).then(() => set('ZI')),
            waitForCameraZoom(camera, -1).then(() => set('ZO')),
        ]);
    }

    deleteAllText(objects);
    deleteAllShapes(objects);

    dispatch(confettiScreen, [0, .6, 45, 1000]);
    dispatch(confettiScreen, [1, .6, 135, 1000]);

    await popups.invoke(tutorialPopup(
        'Woohoo!',
        'Now that you know the basics of moving and zooming around let\`s create some sticky notes!',
        'Okay'
    ));
});

const createSticky = TutorialStep('createSticky', async r => {
    const alerts = r.call(AlertManager);
    const camera = r.call(Camera);
    const dispatch = r.call(CommandDispatcher);
    const injector = r.call(ContentInjector);
    const objects = r.call(ObjectManager);

    const bounds = Phys.from(objects.getAll(Zone)).bounds().inflate(200);
    const bounds2 = {
        ...bounds,
        left: bounds.left - 250,
        top: bounds.top - 100,
    };
    await camera.animateToFrame(bounds2, 2000, easeInOutQuint);

    injector.injectNative(CreateInstructions);
    injector.injectNative(VideoProp2);
    dispatch(clearSelection);
    await popInInstructions(r);

    const setGoal = createTutorialGoals(alerts, [
        TutorialGoal('ATG', 'Add a sticky note to good things'),
        TutorialGoal('WS', 'Write on your sticky note'),
        TutorialGoal('ATB', 'Move your sticky note to bad things'),
    ],
        [2, 8]);

    const zoneArray = Array.from(objects.getAll(Zone));
    const goodZone = zoneArray.find(z => z.title === 'Good things');

    await waitForState(objects, () => objects.get(goodZone.id).children.filter(c => c instanceof Sticky).length);
    setGoal('ATG', true);

    await waitForState(objects, () => Array.from(objects.getAll(Sticky))[0].content.length > 0);
    setGoal('WS', true);

    const badZone = zoneArray.find(z => z.title === 'Bad things');
    await waitForState(objects, () => objects.get(badZone.id).children.filter(c => c instanceof Sticky).length);
    setGoal('ATB', true);

    await waitFor(100);

    deleteAllText(objects);
    deleteAllShapes(objects);
});

const manipulateSticky = TutorialStep('manipulateSticky', async r => {
    const alerts = r.call(AlertManager);
    const camera = r.call(Camera);
    const dispatch = r.call(CommandDispatcher);
    const injector = r.call(ContentInjector);
    const objects = r.call(ObjectManager);
    const session = r.call(EditorSession);

    const bounds = Phys.from(objects.getAll(Zone)).bounds().inflate(200);
    const bounds2 = {
        ...bounds,
        left: bounds.left + 250,
        top: bounds.top - 100,
    };
    await camera.animateToFrame(bounds2, 2000, easeInOutQuint);

    injector.injectNative(SelectInstructions);
    injector.injectNative(VideoProp3);
    dispatch(clearSelection);

    await popInInstructions(r);

    const setGoal = createTutorialGoals(alerts, [
        TutorialGoal('CNS', 'Create a new sticky note in Bad Things'),
        TutorialGoal('MSS', 'Multi-select your sticky notes'),
    ],
        [4, 8]);

    const badZone = Array.from(objects.getAll(Zone)).find(z => z.title === 'Bad things');

    await waitForState(objects, () => objects.get(badZone.id).children.filter(c => c instanceof Sticky).length >= 2);
    setGoal('CNS', true);

    await new Promise<void>((rs) => {
        const off = session.observe(() => {
            const selection = session.get(Selection);

            if (selection.size > 1) {
                setGoal('MSS', true);
                off();
                rs();
            };
        });
    });

    await waitFor(100);

    deleteAllText(objects);
});

const deleteSticky = TutorialStep('deleteSticky', async r => {
    const alerts = r.call(AlertManager);
    const camera = r.call(Camera);
    const dispatch = r.call(CommandDispatcher);
    const injector = r.call(ContentInjector);
    const objects = r.call(ObjectManager);
    const popups = r.call(PopupManager);
    const session = r.call(EditorSession);

    injector.injectNative(DeleteInstructions);
    dispatch(clearSelection);

    await popInInstructions(r);

    const physcials = Array.from(objects.getAll(Physical)).filter(p => p instanceof Sticky);
    session.apply(Selection, s => s.replace(physcials));

    const setGoal = createTutorialGoals(alerts, [
        TutorialGoal('DS', 'Delete the sticky notes'),
    ],
        [5, 8]);

    await waitForState(objects, () => objects.getAll(Sticky).size === 0);
    setGoal('DS', true);

    const bounds = Phys.from(objects.getAll(Zone)).bounds().inflate(200);
    await camera.animateToFrame(bounds, 2000, easeInOutQuint);

    deleteAllText(objects);
    deleteAllShapes(objects);

    dispatch(confettiScreen, [0, .6, 45, 1000]);
    dispatch(confettiScreen, [1, .6, 135, 1000]);

    await popups.invoke(tutorialPopup(
        'Awesome!',
        [
            'I think we can both agree you have mastered sticky notes.',
            'Collaboration is key to an engaging meeting, so let’s see how Ludi works with other people in the board.',
        ],
        'Invite Friends'
    ));

    await waitFor(100);

});

const revealSticky = TutorialStep('revealSticky', async r => {
    const alerts = r.call(AlertManager);
    const camera = r.call(Camera);
    const dispatch = r.call(CommandDispatcher);
    const injector = r.call(ContentInjector);
    const popups = r.call(PopupManager);
    const ptpts = r.call(ParticipantManager);
    const objects = r.call(ObjectManager);

    dispatch(kioskPlayRecording, ['PositiveSticky1']);
    await waitFor(500);
    dispatch(kioskPlayRecording, ['PositiveSticky2']);
    await waitFor(500);
    dispatch(kioskPlayRecording, ['PositiveSticky3']);
    await waitFor(500);
    dispatch(kioskPlayRecording, ['NegativeSticky1']);
    await waitFor(500);
    dispatch(kioskPlayRecording, ['NegativeSticky2']);
    await waitFor(1000);

    await popups.invoke(tutorialPopup(
        'Realtime Collaboration',
        'In Ludi, you see your team mates writing in realtime! ' +
        'However, their sticky notes are unreadable until they decide they are ready to share their feedback.',
        'Next'
    ));

    const bounds = Phys.from(objects.getAll(Zone)).bounds().inflate(200);
    const bounds2 = {
        ...bounds,
        left: bounds.left - 250,
        top: bounds.top - 100,
    };
    await camera.animateToFrame(bounds2, 2000, easeInOutQuint);

    injector.injectNative(RevealInstructions);
    injector.injectNative(VideoProp2);
    dispatch(clearSelection);

    await popInInstructions(r);

    const setGoal = createTutorialGoals(alerts, [
        TutorialGoal('WS', 'Write a sticky note'),
        TutorialGoal('SS', 'Share your sticky note'),
    ],
        [6, 8]);

    await waitForState(objects, () => Array.from(objects.getAll(Sticky)).find(s => s.userId === ptpts.getSelf().id)?.content.length > 0);
    setGoal('WS', true);

    await waitForState(objects, () => {
        return Array.from(objects.getAll(Sticky)).find(s => s.userId === ptpts.getSelf().id)?.concealed === false;
    });
    setGoal('SS', true);

    const stickies = Array.from(objects.getAll(Sticky));
    await revealStickiesWithDelay(objects, stickies);

    deleteAllText(objects);
    deleteAllShapes(objects);

    dispatch(confettiScreen, [0, .6, 45, 1000]);
    dispatch(confettiScreen, [1, .6, 135, 1000]);

    await popups.invoke(tutorialPopup(
        'Awesome!',
        [
            'Ludi is full of great features for collaboration but getting your teams attention is equally important.',
            'For this we have the ping action.',
        ],
        'Show me'
    ));
});

const pingSticky = TutorialStep('pingSticky', async r => {
    const alerts = r.call(AlertManager);
    const dispatch = r.call(CommandDispatcher);
    const injector = r.call(ContentInjector);
    const objects = r.call(ObjectManager);
    const popups = r.call(PopupManager);
    const ptpts = r.call(ParticipantManager);
    const store = r.call(PingStore);

    injector.injectNative(PingInstructions);
    injector.injectNative(VideoProp2);
    dispatch(clearSelection);

    await popInInstructions(r);

    const setGoal = createTutorialGoals(alerts, [
        TutorialGoal('PS', 'Ping your sticky note'),
    ],
        [7, 8]);

    await new Promise<void>((rs) => {
        const off = store.observe((x) => {
            const ping = x.find(p => p.id) as CanvasPing;

            const sticky = Array.from(objects.getAll(Sticky)).find(s => s.userId === ptpts.getSelf().id);

            if (!!sticky && sticky.bounds(RelativeTo.Model).contains({
                ...ping.region,
                top: ping.region.top + 8,
            })) {
                setGoal('PS', true);
                off();
                rs();
            };
        });
    });

    await waitFor(100);

    deleteAllText(objects);
    deleteAllShapes(objects);

    dispatch(confettiScreen, [0, .6, 45, 1000]);
    dispatch(confettiScreen, [1, .6, 135, 1000]);

    await popups.invoke(tutorialPopup(
        'Good Stuff!',
        'We\'ve got one more thing to show you and it\'s kind of a big deal. Our confetti is loved by all teams.',
        'I would like to know more'
    ));
});

const confetti = TutorialStep('confetti', async r => {
    const alerts = r.call(AlertManager);
    const cannon = r.call(ConfettiCannon);
    const dispatch = r.call(CommandDispatcher);
    const injector = r.call(ContentInjector);
    const objects = r.call(ObjectManager);

    injector.injectNative(ConfettiInstructions);
    injector.injectNative(VideoProp2);
    dispatch(clearSelection);

    await popInInstructions(r);

    const setGoal = createTutorialGoals(alerts, [
        TutorialGoal('FC', 'Celebrate with confetti'),
    ],
        [8, 8]);

    dispatch(pingUI, ['toolbox-gestures']);

    await waitForConfetti(cannon);
    setGoal('FC', true);

    await waitFor(500);

    deleteAllText(objects);
    deleteAllShapes(objects);
});

const outro = TutorialStep('outro', async r => {
    const objects = r.call(ObjectManager);
    const popups = r.call(PopupManager);

    const zones = objects.getAll(Zone);
    objects.update(u =>
        zones.forEach(z => {
            u.delete(z, 'cascade');
        })
    );

    await popups.invoke(tutorialPopup(
        'Congratulations!',
        'You\'ve made it to the end, we hope you enjoy exploring more of what Ludi has to offer.',
        'Exit'
    ));
});

export const ParticipantTutorial = [
    intro,
    startMeeting,
    panZoom,
    createSticky,
    manipulateSticky,
    deleteSticky,
    revealSticky,
    pingSticky,
    confetti,
    outro,
];
